import { useState, useMemo, useCallback, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Upload, Users, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AddCompanyDrawer } from "@/components/add/AddCompanyDrawer";
import { ImportDialog } from "@/components/import/ImportDialog";
import { NameCell } from "@/components/list/NameCell";
import { AirtableGrid, type Column } from "@/components/data-grid";
import { ListPager } from "@/components/data-grid/ListPager";
import { WidgetPanel } from "@/components/dashboard-grid/WidgetPanel";
import { usePagedRows } from "@/hooks/usePagedRows";
import { useCoiPeople } from "@/hooks/useCois";
import { useBdTracker } from "@/hooks/useBdTracker";
import { CoiBulkActionBar } from "@/components/coi/CoiBulkActionBar";
import { SPECIALTIES, SPECIALTY_LABELS } from "@/components/coi/specialty";
import { BdTiles } from "@/components/coi/bd/BdTiles";
import { BdScoreboard } from "@/components/coi/bd/BdScoreboard";
import { BdWeeklyChart } from "@/components/coi/bd/BdWeeklyChart";
import { BdReferrers } from "@/components/coi/bd/BdReferrers";
import { BdActivityLog } from "@/components/coi/bd/BdActivityLog";
import { LogLunchDialog } from "@/components/coi/bd/LogLunchDialog";
import { Segmented } from "@/components/coi/bd/Segmented";
import { bdHref, readBdParams, staleWho } from "@/components/coi/bd/bdParams";
import { bdPeopleRows, type BdPeopleRow } from "@/components/coi/bd/peopleRows";
import { firstName, lastTouchShort, titleBadge } from "@/components/coi/bd/format";
import { cn } from "@/lib/utils";
import { BD_PERIODS, BD_PERIOD_TABS, type BdPeriod } from "@shared/bdPeriod";

const DELETE_COPY = "This removes the person from the CRM. Their logged activities and synced emails stay; referrals attributed to them keep their type and note but lose the name.";

const referralsCell = (c: BdPeopleRow) => {
  const received = c.introsOpen + c.introsSigned;
  if (received === 0 && c.sentCount === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="whitespace-nowrap" title={c.introsSigned > 0 ? `${c.introsSigned} signed` : undefined}>
      {`${received} in · ${c.sentCount} sent`}
    </span>
  );
};

const peopleColumns = (periodLabel: string): Column<BdPeopleRow>[] => [
  { id: "name", header: "Name", cell: (c) => {
    const sub = [c.firmName, SPECIALTY_LABELS[c.specialty] ?? c.specialty].filter(Boolean).join(" · ") || "—";
    return <NameCell name={c.name} sub={sub} title={`${c.name} · ${sub}`} nameClassName="font-medium" />;
  } },
  { id: "owner", header: "Owner", width: "w-20", cell: (c) => <span className="text-muted-foreground">{c.ownerName ? firstName(c.ownerName) : "—"}</span> },
  { id: "lastTouch", header: "Last touch", width: "w-28", cell: (c) => <span className="text-muted-foreground whitespace-nowrap">{lastTouchShort(c.lastTouch)}</span> },
  { id: "touches", header: periodLabel ? `Touches · ${periodLabel}` : "Touches", width: "w-32", cell: (c) => <span className="tabular-nums">{c.touches || "—"}</span> },
  { id: "referrals", header: "Referrals", width: "w-32", cell: referralsCell },
];

export default function COI() {
  const [, setLocation] = useLocation();
  const { period, who } = readBdParams(useSearch());
  const setParams = (next: { period?: BdPeriod; who?: string }) =>
    setLocation(bdHref(next.period ?? period, next.who ?? who), { replace: true });
  const queryClient = useQueryClient();
  const bd = useBdTracker(period, who);
  const people = useCoiPeople();

  // A stale ?who= (deactivated user, old bookmark) is a 400: fall back to everyone.
  useEffect(() => {
    if (staleWho(who, bd.error)) setParams({ who: "all" });
  }, [bd.error, who]);

  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [lunchOpen, setLunchOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);

  const rows = useMemo(
    () => bdPeopleRows(people.data ?? [], { who, touchesByCoi: bd.data?.touchesByCoi ?? {}, search: q, specialty }),
    [people.data, who, bd.data?.touchesByCoi, q, specialty],
  );
  // Selections can outlive a filter change; only rows still on screen are acted on.
  const visibleSelected = useMemo(() => rows.filter((c) => selected.has(c.id)).map((c) => c.id), [rows, selected]);
  const paged = usePagedRows(rows);
  const columns = useMemo(() => peopleColumns(bd.data?.period.label ?? ""), [bd.data?.period.label]);
  const specialtyCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const c of people.data ?? []) m[c.specialty] = (m[c.specialty] ?? 0) + 1;
    return m;
  }, [people.data]);

  const toggleSelect = useCallback((id: string | number, checked: boolean) => {
    setSelected((prev) => { const n = new Set(prev); if (checked) n.add(String(id)); else n.delete(String(id)); return n; });
  }, []);

  const afterDelete = () => {
    queryClient.invalidateQueries({ queryKey: ["cois"] });
    queryClient.invalidateQueries({ queryKey: ["coi-bd"] });
  };

  // Takes the label off; the person stays a contact at their firm.
  const removeFromCoi = async (c: { id: string; name: string }) => {
    try {
      const r = await fetch(`/api/cois/${c.id}/unmark`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Failed to remove from COI");
      afterDelete(); toast.success(`${c.name} is a contact again, not a COI`);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed to remove from COI"); }
  };

  const bulkDelete = async () => {
    setBulkDeleteBusy(true);
    try {
      const r = await fetch("/api/cois/bulk-delete", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids: visibleSelected }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Bulk delete failed");
      const { deleted } = await r.json();
      toast.success(`Deleted ${deleted} COI${deleted === 1 ? "" : "s"}`);
      setSelected(new Set()); setBulkDeleteOpen(false); afterDelete();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Bulk delete failed"); } finally { setBulkDeleteBusy(false); }
  };

  const exportCSV = () => {
    const subset = rows.filter((c) => visibleSelected.length === 0 || selected.has(c.id));
    const headers = ["Name", "Title", "Specialty", "Firm", "Owner", "Email", "Phone", "Last touch", "Touches in period", "Intros", "Mandates", "Sent to them"];
    const out = subset.map((c) => [c.name, c.title ?? "", SPECIALTY_LABELS[c.specialty] ?? "", c.firmName ?? "", c.ownerName ?? "", c.email ?? "", c.phone ?? "", c.lastTouch?.at ?? "", String(c.touches), String(c.introsOpen + c.introsSigned), String(c.introsSigned), String(c.sentCount)]);
    const csv = [headers, ...out].map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `cois-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const chip = (key: string, label: string) => (
    <button key={key} type="button" aria-pressed={specialty === key} onClick={() => setSpecialty(key)}
      className={cn("h-6 px-2 rounded-full border text-[11px] whitespace-nowrap", specialty === key ? "border-primary bg-primary/10 text-primary-text" : "border-border text-muted-foreground hover:text-foreground")}>
      {label}
    </button>
  );
  const emptyReason = q || specialty !== "all" || who !== "all" ? "No COIs match" : "No COIs yet";
  const team = bd.data?.people ?? [];

  return (
    <AppLayout title="COI" fill>
      <div className="flex flex-col gap-3 lg:flex-1 lg:min-h-0 page-enter">
        <header className="flex items-center gap-3 flex-wrap lg:flex-nowrap shrink-0">
          <h1 className="text-lg font-semibold tracking-tight text-foreground sr-only 2xl:not-sr-only">Centers of Influence</h1>
          <Segmented ariaLabel="Whose work" value={who} onChange={(id) => setParams({ who: id })}
            options={[{ id: "all", label: "Everyone" }, ...team.map((p) => ({ id: p.id, label: firstName(p.name), badge: titleBadge(p.title) }))]} />
          <Segmented ariaLabel="Period" value={period} onChange={(p) => setParams({ period: p })}
            options={BD_PERIODS.map((p) => ({ id: p, label: BD_PERIOD_TABS[p] }))} />
          <div className="ml-auto shrink-0 flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setLunchOpen(true)}><UtensilsCrossed className="w-4 h-4" /> <span className="2xl:hidden">Log lunch</span><span className="hidden 2xl:inline">Log lunch / meeting</span></Button>
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setImportOpen(true)} aria-label="Import" title="Import"><Upload className="w-4 h-4" /> <span className="hidden 2xl:inline">Import</span></Button>
            <Button size="sm" className="h-8 gap-1.5" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4" /> Add COI</Button>
          </div>
        </header>

        {bd.isError && !bd.data && !staleWho(who, bd.error) && (
          <div role="alert" className="shrink-0 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm flex items-center gap-3">
            <p className="flex-1">Couldn't load the BD numbers.</p>
            <button type="button" onClick={() => bd.refetch()} className="font-medium underline">Retry</button>
          </div>
        )}

        <div className="shrink-0">{bd.data ? <BdTiles data={bd.data} /> : <Skeleton className="h-[4.5rem] rounded-lg" />}</div>

        <div className="grid gap-3 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:flex-1 lg:min-h-0">
          <WidgetPanel title="People" meta={String(rows.length)} className="min-h-[28rem] lg:min-h-0"
            action={(
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="relative w-40 shrink-0">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1.5 text-muted-foreground" />
                  <Input placeholder="Search people" className="pl-7 h-6 text-xs" value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <div className="flex gap-1 overflow-x-auto">
                  {chip("all", "All")}
                  {SPECIALTIES.filter((s) => specialtyCounts[s.key]).map((s) => chip(s.key, `${s.plural} ${specialtyCounts[s.key]}`))}
                </div>
              </div>
            )}
            bodyClassName="overflow-hidden flex flex-col">
            <div className="flex flex-col min-h-0 flex-1">
              <div className="shrink-0">
                <CoiBulkActionBar selectedCount={visibleSelected.length} onDelete={() => setBulkDeleteOpen(true)} onExport={exportCSV} onClear={() => setSelected(new Set())} busy={bulkDeleteBusy} />
              </div>
              {people.isLoading ? <div className="py-16 text-center text-muted-foreground">Loading…</div>
                : rows.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground"><Users className="w-10 h-10 mb-3 opacity-30" /><p>{emptyReason}</p></div>
                ) : (
                  <div className="flex-1 min-h-0 flex flex-col">
                  <AirtableGrid
                    density="compact" scroll
                    data={paged.rows} columns={columns} getRowId={(c) => c.id} onRowClick={(c) => setLocation(`/coi/${c.id}`)}
                    getRowActions={(c) => [
                      { label: "Remove from COI", onClick: () => removeFromCoi({ id: c.id, name: c.name }) },
                      { label: "Delete", variant: "destructive" as const, onClick: () => setPendingDelete({ id: c.id, name: c.name }) },
                    ]}
                    selectedRows={selected} onSelectRow={toggleSelect}
                    onSelectAll={(checked) => setSelected(checked ? new Set(rows.map((c) => c.id)) : new Set())}
                  />
                  </div>
                )}
              <div className="shrink-0 mt-auto">
                <ListPager
                  density="compact" shown={paged.rows.length} total={paged.total} noun={["COI", "COIs"]}
                  page={paged.page} totalPages={paged.totalPages} pageSize={paged.pageSize} onPageSizeChange={paged.setPageSize}
                  hasPrev={paged.hasPrev} hasNext={paged.hasNext} onPrev={paged.prev} onNext={paged.next} testIdPrefix="cois"
                />
              </div>
            </div>
          </WidgetPanel>

          <div className="grid gap-3 min-h-0 lg:grid-rows-[minmax(0,1.1fr)_minmax(0,0.8fr)_minmax(0,1.1fr)]">
            <div className="min-h-[14rem] lg:min-h-0">{bd.data ? <BdScoreboard data={bd.data} onSelect={(id) => setParams({ who: id })} /> : <Skeleton className="h-40 lg:h-full rounded-lg" />}</div>
            <div className="h-44 lg:h-auto lg:min-h-0">{bd.data ? <BdWeeklyChart data={bd.data} /> : <Skeleton className="h-full rounded-lg" />}</div>
            <div className="grid gap-3 lg:min-h-0 lg:grid-rows-2 xl:grid-rows-1 xl:grid-cols-2">
              <div className="min-h-[16rem] lg:min-h-0">{bd.data ? <BdReferrers data={bd.data} /> : <Skeleton className="h-full rounded-lg" />}</div>
              <div className="min-h-[16rem] lg:min-h-0">{bd.data ? <BdActivityLog data={bd.data} /> : <Skeleton className="h-full rounded-lg" />}</div>
            </div>
          </div>
        </div>
      </div>

      <LogLunchDialog open={lunchOpen} onOpenChange={setLunchOpen} />
      <AddCompanyDrawer open={addOpen} onOpenChange={setAddOpen} preselectedCategory="coi" />
      <ImportDialog type="coi" open={importOpen} onOpenChange={setImportOpen} />
      <ConfirmDialog
        open={!!pendingDelete} onOpenChange={(n) => !n && !isDeleting && setPendingDelete(null)}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : ""} description={DELETE_COPY}
        confirmLabel="Delete" cancelLabel="Cancel" variant="destructive" isLoading={isDeleting}
        onConfirm={async () => {
          if (!pendingDelete || isDeleting) return;
          setIsDeleting(true);
          try {
            const r = await fetch(`/api/cois/${pendingDelete.id}`, { method: "DELETE", credentials: "include" });
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Failed to delete");
            afterDelete(); toast.success(`Deleted ${pendingDelete.name}`); setPendingDelete(null);
          } catch (e) { toast.error(e instanceof Error ? e.message : "Failed to delete"); } finally { setIsDeleting(false); }
        }}
      />
      <ConfirmDialog
        open={bulkDeleteOpen} onOpenChange={(v) => !v && !bulkDeleteBusy && setBulkDeleteOpen(v)}
        title={`Delete ${visibleSelected.length} COI${visibleSelected.length === 1 ? "" : "s"}?`} description={DELETE_COPY}
        confirmLabel="Delete" cancelLabel="Cancel" variant="destructive" isLoading={bulkDeleteBusy} onConfirm={bulkDelete}
      />
    </AppLayout>
  );
}
